import React, { useState, useEffect, useMemo } from "react";
import "./Users.css";
import Header from "../header/Header";
import authService from "../../services/auth.service";
import Sidebar from "../Sidebar";
import Footer from "../footer/Footer";
import VivahApi from "../../api";

const DEACTIVATE_REASONS = [
  'Fake / suspicious profile',
  'Spam or inappropriate content',
  'User requested via support',
  'Duplicate account',
  'Other',
];

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterProfile, setFilterProfile] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [actionLoading, setActionLoading] = useState(null); // userId being acted on

  const [deactivateModal, setDeactivateModal] = useState(null); // { user } | null
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  React.useEffect(() => {
    try {
      const authService = require('../../services/auth.service').default;
      if (!authService.isAdmin()) window.location.replace('/');
    } catch (e) {
      console.warn('Admin guard failed', e);
    }
  }, []);

  const [adminName, setAdminName] = React.useState('Admin');
  React.useEffect(() => {
    try {
      const user = authService.getCurrentUser();
      if (user && (user.name || user.email)) { setAdminName(user.name || user.email); return; }
      const token = authService.getIdToken();
      if (token) {
        const parts = token.split('.');
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          setAdminName(payload.name || payload.email || 'Admin');
        }
      }
    } catch (e) { console.warn('Failed to load admin name:', e); }
  }, []);

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      try {
        const resp = await VivahApi.Admin.listUsers(1000);
        setUsers(resp.data.users || []);
      } catch (err) {
        console.error('Failed to load admin users', err);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!deactivateModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setDeactivateModal(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [deactivateModal]);

  const filteredUsers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return users.filter(u => {
      if (q && !u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
      if (filterProfile === 'complete' && !u.profileComplete) return false;
      if (filterProfile === 'incomplete' && u.profileComplete) return false;
      if (filterStatus === 'active' && u.status === 'deactivated') return false;
      if (filterStatus === 'deactivated' && u.status !== 'deactivated') return false;
      return true;
    });
  }, [users, searchText, filterProfile, filterStatus]);

  function openDeactivateModal(user) {
    setDeactivateModal({ user });
    setSelectedReason('');
    setCustomReason('');
  }

  async function confirmDeactivate() {
    if (!deactivateModal) return; // guard re-entry
    const reason = selectedReason === 'Other' ? customReason.trim() : selectedReason;
    if (!reason) return;
    const { user } = deactivateModal;
    setDeactivateModal(null);
    setActionLoading(user.userId);
    try {
      await VivahApi.Admin.deactivateUser(user.userId, reason);
      setUsers(prev => prev.map(u =>
        u.userId === user.userId
          ? { ...u, status: 'deactivated', deActivateReason: reason, deactivatedAt: new Date().toISOString(), deactivatedSource: 'admin' }
          : u
      ));
    } catch (err) {
      alert('Failed to deactivate user: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleActivate(user) {
    setActionLoading(user.userId);
    try {
      await VivahApi.Admin.activateUser(user.userId);
      setUsers(prev => prev.map(u =>
        u.userId === user.userId ? { ...u, status: 'active' } : u
      ));
    } catch (err) {
      alert('Failed to activate user: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  }

  function deactivationTooltip(u) {
    if (u.status !== 'deactivated') return undefined;
    const by = u.deactivatedSource === 'admin' ? 'Admin' : 'Self';
    const reason = u.deActivateReason || 'Reason not recorded';
    const date = u.deactivatedAt
      ? new Date(u.deactivatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';
    return `Deactivated by: ${by}\nReason: ${reason}\nDate: ${date}`;
  }

  function formatDate(ts) {
    if (!ts) return '—';
    return new Date(Number(ts)).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  return (
    <>
      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />
        <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">Dashboard</h1>
              </div>
            </div>
          </div>
        </div>
        <section id="gla_content" className="gla_content">
          <section className="gla_section">
            <div className="dashboard">
              <div className="row">
                <Sidebar />
                <div className="col-md-10 col-xs-12 dashboard_container">
                  <div className="admin-users-header">Users</div>

                  <div className="admin-filter-bar">
                    <input
                      className="admin-filter-search"
                      type="text"
                      placeholder="Search name or email..."
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                    />
                    <select
                      className="admin-filter-select"
                      value={filterProfile}
                      onChange={e => setFilterProfile(e.target.value)}
                    >
                      <option value="all">Profile: All</option>
                      <option value="complete">Complete</option>
                      <option value="incomplete">Incomplete</option>
                    </select>
                    <select
                      className="admin-filter-select"
                      value={filterStatus}
                      onChange={e => setFilterStatus(e.target.value)}
                    >
                      <option value="all">Status: All</option>
                      <option value="active">Active</option>
                      <option value="deactivated">Deactivated</option>
                    </select>
                  </div>
                  <div className="admin-filter-count">
                    Showing {filteredUsers.length} of {users.length} users
                  </div>

                  {loading ? (
                    <div>Loading...</div>
                  ) : (
                    <table className="admin-users-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Joined</th>
                          <th>Profile</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(u => (
                          <tr key={u.userId} className={u.status === 'deactivated' ? 'row-deactivated' : ''}>
                            <td>{u.name || '—'}</td>
                            <td>{u.email}</td>
                            <td>{formatDate(u.createdAt)}</td>
                            <td>{u.profileComplete ? '✓' : '✗'}</td>
                            <td>
                              <span
                                className={`status-badge status-${u.status === 'deactivated' ? 'deactivated' : 'active'}`}
                                title={deactivationTooltip(u)}
                                style={u.status === 'deactivated' ? { cursor: 'help' } : undefined}
                              >
                                {u.status === 'deactivated' ? 'Deactivated' : 'Active'}
                              </span>
                            </td>
                            <td>
                              {u.status === 'deactivated' ? (
                                <button
                                  className="btn-activate"
                                  disabled={actionLoading === u.userId}
                                  onClick={() => handleActivate(u)}
                                >
                                  {actionLoading === u.userId ? '...' : 'Activate'}
                                </button>
                              ) : (
                                <button
                                  className="btn-deactivate"
                                  disabled={actionLoading === u.userId}
                                  onClick={() => openDeactivateModal(u)}
                                >
                                  {actionLoading === u.userId ? '...' : 'Deactivate'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </section>
        </section>
        <Footer />
        {deactivateModal && (
          <div className="deactivate-modal-overlay" onClick={() => setDeactivateModal(null)}>
            <div className="deactivate-modal" onClick={e => e.stopPropagation()}>
              <h3 className="deactivate-modal-title">
                Deactivate {deactivateModal.user.name || deactivateModal.user.email}?
              </h3>
              <p className="deactivate-modal-sub">Select a reason. The user will lose access immediately.</p>
              <div className="deactivate-modal-reasons">
                {DEACTIVATE_REASONS.map(r => (
                  <label key={r} className="deactivate-modal-reason">
                    <input
                      type="radio"
                      name="deactivate-reason"
                      value={r}
                      checked={selectedReason === r}
                      onChange={() => { setSelectedReason(r); setCustomReason(''); }}
                    />
                    {r}
                  </label>
                ))}
              </div>
              {selectedReason === 'Other' && (
                <textarea
                  className="deactivate-modal-custom"
                  placeholder="Enter reason (max 200 chars)…"
                  maxLength={200}
                  value={customReason}
                  onChange={e => setCustomReason(e.target.value)}
                  rows={3}
                />
              )}
              <div className="deactivate-modal-actions">
                <button className="deactivate-modal-cancel" onClick={() => setDeactivateModal(null)}>
                  Cancel
                </button>
                <button
                  className="deactivate-modal-confirm"
                  disabled={!selectedReason || (selectedReason === 'Other' && !customReason.trim())}
                  onClick={confirmDeactivate}
                >
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminUsers;
